/**
 * AppLayout — header with mode badge + lang switcher + logout, then a
 * scrollable main slot for the page content. Used by every authenticated
 * page so behaviour stays consistent.
 */
import { ReactNode } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Package, LogOut, Languages } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useModeStore } from '@/stores/modeStore';

const MODE_LABEL: Record<string, string> = {
  sender: 'مرسِل',
  driver: 'سائق',
  center: 'مركز شحن',
  receiver: 'مستلِم',
  company: 'شركة شحن',
};

export function AppLayout({ children }: { children: ReactNode }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();
  const { mode, clear: clearMode } = useModeStore();

  async function handleLogout() {
    await logout();
    clearMode();
    navigate('/login', { replace: true });
  }

  function cycleLang() {
    const next = i18n.language === 'ar' ? 'en' : i18n.language === 'en' ? 'tr' : 'ar';
    void i18n.changeLanguage(next);
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <header className="bg-surface border-b border-border sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/home" className="flex items-center gap-2 text-primary hover:text-primary-light">
            <Package size={22} />
            <span className="font-bold text-lg hidden sm:inline">{t('app_name', 'كاروَتس كارقو')}</span>
          </Link>
          <div className="flex items-center gap-2">
            {mode && (
              <button
                onClick={() => navigate('/mode-selector')}
                className="text-xs bg-primary/10 text-primary px-3 py-1.5 rounded-full font-semibold hover:bg-primary/15"
                title={t('change_mode', 'تغيير الوضع')}
              >
                {MODE_LABEL[mode] || mode}
              </button>
            )}
            <button
              onClick={cycleLang}
              className="p-2 rounded-lg hover:bg-background text-text-secondary"
              title={i18n.language.toUpperCase()}
            >
              <Languages size={18} />
            </button>
            {user && (
              <button
                onClick={handleLogout}
                className="p-2 rounded-lg hover:bg-error/10 text-text-secondary hover:text-error"
                title={t('logout', 'تسجيل الخروج')}
              >
                <LogOut size={18} />
              </button>
            )}
          </div>
        </div>
      </header>
      <main className="flex-1 max-w-6xl w-full mx-auto px-4 py-6">{children}</main>
    </div>
  );
}
